/**
 * PIX key helpers shared by the dispersions Brazil panel and the client-account form (BRL|Pix), so both
 * surfaces mask, hint and validate the key identically. Extracted from the Step 2 recipient panel.
 *
 * User-facing copy lives in the `global` i18n namespace (shared across modules): `PIX_KEY_META` carries the
 * translation KEYS, so this util stays hook-free — components translate via `useTranslations("global")` and
 * pure validators via the `i18n` singleton.
 */

import type { GlobalTranslations } from "@/shared/lang/interfaces/global";
import { isValidCpf } from "@/shared/utils/brazilDocument";

export type PixKeyTypeEnum = "CPF" | "CNPJ" | "EMAIL" | "PHONE" | "EVP";

/**
 * Maps the legacy BRL|Pix representations to the enum: the numeric select codes ("0".."4") used by the
 * country-field config / account form, plus the stored-account label whose uppercase is not the enum value
 * (`tipoLlavePix: "Teléfono"` — see buildCreateClientAccountPayload).
 */
export const PIX_KEY_TYPE_BY_LEGACY: Record<string, PixKeyTypeEnum> = {
  "0": "CPF",
  "1": "CNPJ",
  "2": "EMAIL",
  "3": "PHONE",
  "4": "EVP",
  TELÉFONO: "PHONE",
};

/** Resolves any legacy representation (code, label, enum, any casing) to the enum; undefined if unknown. */
export function resolvePixKeyType(raw: string | null | undefined): PixKeyTypeEnum | undefined {
  const value = (raw ?? "").trim();
  if (!value) return undefined;
  const upper = value.toUpperCase();
  const mapped = PIX_KEY_TYPE_BY_LEGACY[value] ?? PIX_KEY_TYPE_BY_LEGACY[upper];
  if (mapped) return mapped;
  return upper in PIX_KEY_META ? (upper as PixKeyTypeEnum) : undefined;
}

/** Per-type placeholder / helper / error copy as `global`-namespace translation keys. */
export const PIX_KEY_META: Record<
  PixKeyTypeEnum,
  {
    ph: keyof GlobalTranslations;
    helper: keyof GlobalTranslations;
    err: keyof GlobalTranslations;
  }
> = {
  CPF: { ph: "pix_key_ph_cpf", helper: "pix_key_helper_cpf", err: "pix_key_err_cpf" },
  CNPJ: { ph: "pix_key_ph_cnpj", helper: "pix_key_helper_cnpj", err: "pix_key_err_cnpj" },
  EMAIL: { ph: "pix_key_ph_email", helper: "pix_key_helper_email", err: "pix_key_err_email" },
  PHONE: { ph: "pix_key_ph_phone", helper: "pix_key_helper_phone", err: "pix_key_err_phone" },
  EVP: { ph: "pix_key_ph_evp", helper: "pix_key_helper_evp", err: "pix_key_err_evp" },
};

/** Progressive display mask while typing (CPF/CNPJ/PHONE) — EMAIL/EVP are lowercased instead. */
export function formatPixInput(type: PixKeyTypeEnum, raw: string): string {
  if (type === "EMAIL") return raw.toLowerCase().slice(0, 80);
  if (type === "EVP")
    return raw
      .toLowerCase()
      .replace(/[^0-9a-f-]/g, "")
      .slice(0, 36);
  let d = raw.replace(/\D/g, "");
  if (type === "CPF") {
    d = d.slice(0, 11);
    if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
    return d;
  }
  if (type === "CNPJ") {
    d = d.slice(0, 14);
    if (d.length > 12)
      return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
    if (d.length > 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
    return d;
  }
  if (type === "PHONE") {
    d = d.slice(0, 11);
    // Brazilian split: 10-digit landlines group 4+4 after the area code, 11-digit mobiles 5+4. While
    // typing, keep the landline split until the 11th digit arrives (standard shifting phone mask).
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length > 6) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    if (d.length > 2) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return d;
  }
  return raw;
}

/**
 * Mask-tolerant validity check — accepts both the masked display value and the raw key. EMAIL/EVP are
 * case-insensitive on purpose: typed input is lowercased by `formatPixInput`, but LEGACY stored keys
 * autofilled from client accounts may carry uppercase (the old payments validation accepted them) and
 * `normalizePixKey` lowercases before storage/transport anyway.
 */
export function pixKeyValid(type: PixKeyTypeEnum, key: string): boolean {
  const d = key.replace(/\D/g, "");
  if (type === "CPF") return d.length === 11;
  if (type === "CNPJ") return d.length === 14;
  if (type === "EMAIL") return /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(key);
  if (type === "PHONE") return d.length === 10 || d.length === 11;
  if (type === "EVP") return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
  return false;
}

/**
 * Raw key for storage/transport: digits only for CPF/CNPJ/PHONE, lowercase for EMAIL/EVP — the shape account
 * metadata (`llavePix`) and the payout providers expect.
 */
export function normalizePixKey(type: PixKeyTypeEnum, key: string): string {
  const trimmed = key.trim();
  if (type === "CPF" || type === "CNPJ" || type === "PHONE") return trimmed.replace(/\D/g, "");
  return trimmed.toLowerCase();
}

/**
 * Guesses the key type from what was typed or pasted, so the person does not have to pick it first. Returns
 * the type and the value to keep in the field (a `+55` dropped from a phone). Null when the shape says
 * nothing yet: the caller keeps the type already chosen.
 *
 * Eleven digits are a CPF and a mobile at once; a valid CPF check digit decides, so a phone with a valid CPF
 * shape still needs its chip picked by hand.
 */
export function detectPixKey(raw: string): { type: PixKeyTypeEnum; value: string } | null {
  const value = raw.trim();
  if (!value) return null;
  if (value.includes("@")) return { type: "EMAIL", value };
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return { type: "EVP", value };
  }
  if (/[a-z]/i.test(value)) return null;
  const digits = value.replace(/\D/g, "");
  const looksLikePhone = value.startsWith("+") || value.includes("(");
  if (looksLikePhone || ((digits.length === 12 || digits.length === 13) && digits.startsWith("55"))) {
    const local = digits.startsWith("55") && digits.length > 11 ? digits.slice(2) : digits;
    return { type: "PHONE", value: local };
  }
  if (digits.length === 14) return { type: "CNPJ", value: digits };
  if (digits.length === 11) return { type: isValidCpf(digits) ? "CPF" : "PHONE", value: digits };
  if (digits.length === 10) return { type: "PHONE", value: digits };
  return null;
}
